import { useEffect, useCallback } from 'react';
import { toast } from 'sonner';

/**
 * useKeyboardShortcuts - Global keyboard shortcuts for WriteRight
 *
 * Shortcuts:
 *  Ctrl+Enter    → Generate docs
 *  Ctrl+Shift+C  → Copy all (note + ICD-10)
 *  Ctrl+Shift+N  → Next patient
 *  Ctrl+Shift+M  → Toggle Minimalist (Calm-Mode) View   (Feb 27, 2026)
 *  Ctrl+/        → Show shortcut legend
 */
const useKeyboardShortcuts = ({
  onGenerateDocs,
  onCopyAll,
  onNextPatient,
  onToggleLegend,
  isGenerating = false,
}) => {
  const handleKeyDown = useCallback((e) => {
    // Don't trigger inside modals or dialogs. Guard for synthetic events
    // (or unusual targets like window/document) where `.closest()` may not
    // exist — without this guard the whole handler throws and silently
    // breaks every shortcut. Feb 27, 2026 RCA.
    const inDialog = e.target && typeof e.target.closest === 'function'
      ? e.target.closest('[role="dialog"]')
      : null;
    if (inDialog) return;

    const isCtrl = e.ctrlKey || e.metaKey;

    // Ctrl+Enter → Generate docs
    if (isCtrl && e.key === 'Enter') {
      e.preventDefault();
      if (onGenerateDocs && !isGenerating) onGenerateDocs();
      return;
    }

    // Ctrl+Shift+C → Copy all
    if (isCtrl && e.shiftKey && (e.key === 'C' || e.key === 'c')) {
      e.preventDefault();
      if (onCopyAll) onCopyAll();
      return;
    }

    // Ctrl+Shift+N → Next patient
    if (isCtrl && e.shiftKey && (e.key === 'N' || e.key === 'n')) {
      e.preventDefault();
      if (onNextPatient) onNextPatient();
      return;
    }

    // Ctrl+Shift+M → Toggle Minimalist (Calm-Mode) View (Feb 27, 2026).
    // Flips the same localStorage flag + html data-attr that the Profile
    // Settings toggle controls — instant calm-mode mid-encounter without
    // losing your place to a modal.
    if (isCtrl && e.shiftKey && (e.key === 'M' || e.key === 'm')) {
      e.preventDefault();
      try {
        const cur = localStorage.getItem('writeright_minimalist_tiles') === '1';
        const next = cur ? '0' : '1';
        localStorage.setItem('writeright_minimalist_tiles', next);
        document.documentElement.setAttribute('data-minimalist', next);
        // Feb 28, 2026 — broadcast so the new MinimalistPill in the header
        // re-reads localStorage and flips its visual state.
        try { window.dispatchEvent(new CustomEvent('writeright:minimalist-changed')); } catch { /* ignore */ }
        toast.success(
          next === '1' ? 'Minimalist (Calm-Mode) ON' : 'Minimalist (Calm-Mode) OFF',
          { duration: 1500 }
        );
      } catch { /* ignore */ }
      return;
    }

    // Ctrl+/ → Toggle shortcut legend
    if (isCtrl && e.key === '/') {
      e.preventDefault();
      if (onToggleLegend) onToggleLegend();
      return;
    }
  }, [onGenerateDocs, onCopyAll, onNextPatient, onToggleLegend, isGenerating]);

  useEffect(() => {
    // Feb 27, 2026 — bind to document with capture phase. window/bubble
    // listener was getting swallowed somewhere in the React tree (possibly
    // by Sentry's wrapped event handlers); capture-phase on document
    // catches every keypress regardless of focus or stopPropagation calls.
    document.addEventListener('keydown', handleKeyDown, true);
    return () => document.removeEventListener('keydown', handleKeyDown, true);
  }, [handleKeyDown]);
};

export default useKeyboardShortcuts;
